import React from 'react';
import { ExternalLink, BedDouble, Compass, ShieldCheck, Smartphone, Plane, Wallet, Car, Tag, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMonetization } from '../context/MonetizationContext';
import { trackAffiliateClick } from '../api';

const CAT_ICON = {
  hotels: BedDouble,
  tours: Compass,
  insurance: ShieldCheck,
  esim: Smartphone,
  flights: Plane,
  finance: Wallet,
  transfers: Car,
  other: Tag,
};

// Renders affiliate partner cards for a given placement key.
// title/subtitle optional. Shows nothing if no partners match.
const AffiliateBlock = ({ placement, title = 'Travel essentials', subtitle, columns = 3, className = '' }) => {
  const { affiliatesFor } = useMonetization();
  const items = affiliatesFor(placement);
  if (!items.length) return null;

  const colClass = columns === 2 ? 'sm:grid-cols-2' : columns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className={className}>
      {title && (
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <span className="text-[12px] font-semibold tracking-[0.16em] uppercase text-[#e0564e]">Recommended</span>
            <h3 className="mt-1 font-display text-[24px] font-semibold text-[#1B1D21]">{title}</h3>
            {subtitle && <p className="mt-1 text-[14px] text-[#4B4F58]">{subtitle}</p>}
          </div>
        </div>
      )}
      <div className={`grid gap-4 ${colClass}`}>
        {items.map((a) => {
          const Icon = CAT_ICON[a.category] || Tag;
          return (
            <a
              key={a.id}
              href={a.url || '#'}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => trackAffiliateClick(a.id)}
              className={`lift group relative flex flex-col rounded-2xl bg-white p-5 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)] ${a.badge ? 'border-2 border-[#B68A35] ring-1 ring-[#B68A35]/20' : 'border border-[#E6D7A2]'}`}
            >
              {a.badge && (
                <span className="deal-ribbon absolute -top-3 right-4 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide uppercase rounded-full sunset-gradient text-white px-3 py-1.5 shadow-[0_8px_18px_-6px_rgba(182,138,53,0.7)]">
                  <Sparkles className="w-3.5 h-3.5" /> {a.badge}
                </span>
              )}
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl sunset-gradient text-white shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <span className="font-display text-[18px] font-semibold text-[#1B1D21] leading-tight">{a.name}</span>
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-[#4B4F58] grow">{a.blurb}</p>
              <div className="mt-4 flex items-center justify-between gap-3">
                {a.priceLabel ? <span className="text-[13px] font-semibold text-[#2F663C]">{a.priceLabel}</span> : <span />}
                <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#92722A] group-hover:text-[#1B1D21] transition-colors">
                  {a.cta} <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </div>
            </a>
          );
        })}
      </div>
      <p className="mt-3 text-[12px] text-[#8a7a63]">
        Some links are affiliate links — we may earn a commission at no extra cost to you.{' '}
        <Link to="/about" className="underline underline-offset-2 hover:text-[#1B1D21]">Disclosure</Link>.
      </p>
    </section>
  );
};

export default AffiliateBlock;
